import React from 'react';
import { compose } from 'redux';
import { withRouter, useLocation } from 'react-router-dom';
import { injectIntl } from '../../util/reactIntl';
import { connect } from 'react-redux';
import { types as sdkTypes } from '../../util/sdkLoader';
import { findOptionsForSelectFilter } from '../../util/search';
import config from '../../config';
import businessCategoryConfig from '../../business-category-config';
import { createResourceLocatorString } from '../../util/routes';
import routeConfiguration from '../../routeConfiguration';
import { parse } from '../../util/urlHelpers';

import { getMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import { manageDisableScrolling, isScrollingDisabled } from '../../ducks/UI.duck';
import { prependNewProduct } from '../ConsoleProductCatalogPage/ConsoleProductCatalogPage.slice';

import { Page } from '../../components';
import { EditProductForm } from '../../forms';
import { TopbarContainer } from '../../containers';

import {
  requestCreateProduct,
  requestUpdateProduct,
  requestImageUpload,
  updateImageOrder,
  removeProductImage,
  loadData,
} from './EditProductPage.slice';

import css from './EditProductPage.css';

const { UUID } = sdkTypes;

export const EditProductPageComponent = props => {
  const {
    createStripeAccountError = null,
    fetchInProgress,
    getOwnProduct,
    history,
    intl,
    onImageUpload,
    onRemoveProductImage,
    onManageDisableScrolling,
    onUpdateImageOrder,
    page,
    params,
    scrollingDisabled,
    onCreateProduct,
    onUpdateProduct,
    currentUser = null,
    onPrependNewProduct
  } = props;

  const { id, type } = params;
  const isNewURI = type === 'new';
  const isNewListingFlow = isNewURI;
  const location = useLocation();

  const productId = isNewURI ? null : (page.submittedProductId || (id ? new UUID(id) : null));
  const currentListing = isNewURI ? null : getOwnProduct(productId);

  const showForm = isNewURI || currentListing?.id;

  if (showForm) {
    const currentListingImages =
      currentListing && currentListing.images ? currentListing.images : [];

    const imageOrder = page.imageOrder || [];
    const unattachedImages = Object.keys(page.images).map(k => page.images[k]);

    const allImages = currentListingImages.concat(unattachedImages);
    const removedImageIds = page.removedImageIds || [];
    const images = allImages.filter(img => {
      return !removedImageIds.includes(img.id);
    }).sort((a, b) => {
      if (!a) return -1;
      return imageOrder.indexOf(a.id.uuid) - imageOrder.indexOf(b.id.uuid);
    });

    const categoryOptions = findOptionsForSelectFilter('category', config.custom.filters);

    const pageTitle = isNewListingFlow
      ? intl.formatMessage({ id: 'EditProductPage.titleCreateProduct' })
      : intl.formatMessage({ id: 'EditProductPage.titleEditProduct' });

    const { countryId, title, description, modelCode, parts, minBookingDays, businessCategory, price, externalLink } = currentListing?.attributes || {};
    const initialCategory = businessCategoryConfig.find(item => item.key === businessCategory)?.category;
    const { updateProductInProgress, createProductInProgress } = page;

    const saveActionMsg = isNewListingFlow ? intl.formatMessage({ id: 'EditProductPage.add' }) : intl.formatMessage({ id: 'EditProductPage.update' });

    const redirectToCatalogWithHighlight = (productId, isNew = false) => {
      const routes = routeConfiguration();
      const currentSearchParams = parse(location.search);

      let targetSearchParams = {};

      if (isNew) {
        targetSearchParams = {
          page: 1,
          countryId: currentSearchParams.countryId, 
          highlightedProductId: productId,
        };
      } else {
        targetSearchParams = {
          ...currentSearchParams,
          page: currentSearchParams.page || 1,
          highlightedProductId: productId,
        };
      }

      const catalogURI = createResourceLocatorString(
        'ConsoleProductCatalogPage',
        routes,
        {},
        targetSearchParams
      );
      history.push(catalogURI);
    };

    return (
      <Page title={pageTitle} scrollingDisabled={scrollingDisabled}>
        <TopbarContainer
          className={css.topbar}
          mobileRootClassName={css.mobileTopbar}
          desktopClassName={css.desktopTopbar}
          mobileClassName={css.mobileTopbar}
        />
        <div className={css.root}>
          <div className={css.headerWrapper}>
            <h1 className={css.title}>
              {pageTitle}
            </h1>
          </div>
          <EditProductForm
            className={css.form}
            initialValues={{ countryId, title, category: initialCategory, description, modelCode, externalLink, parts, minBookingDays, businessCategory, price, images }}
            images={images}
            saveActionMsg={saveActionMsg}
            onSubmit={values => {
              const { countryId, title, description, modelCode, parts, externalLink, minBookingDays, businessCategory, images, price } = values;
              const updateValues = {
                countryId,
                title: title.trim(),
                description,
                businessCategory,
                modelCode,
                externalLink,
                parts,
                minBookingDays: parseInt(minBookingDays),
                imageIds: images.map(i => (typeof i.id === 'string' ? i.imageId.uuid : i.id.uuid)),
                price: price.amount
              };
              if (currentListing?.id?.uuid) {
                onUpdateProduct({ id: currentListing.id.uuid, ...updateValues })
                  .then(() => redirectToCatalogWithHighlight(currentListing.id.uuid, false));
              } else {
                onCreateProduct(updateValues)
                  .then(res => {
                    const newProductData = res.payload.data.data;
                    const newProductRef = { id: newProductData.id, type: newProductData.type };
                    onPrependNewProduct(newProductRef);
          
                    redirectToCatalogWithHighlight(res.payload.data.data.id.uuid, true)});
              }
            }}
            disabled={false}
            ready={false}
            updated={false}
            submitInProgress={updateProductInProgress || createProductInProgress}
            fetchErrors={null}
            categories={categoryOptions}
            onImageUpload={onImageUpload}
            onRemoveImage={onRemoveProductImage}
            onUpdateImageOrder={onUpdateImageOrder}
            currentUser={currentUser}
            keepDirtyOnReinitialize
          />
        </div>
      </Page>
    );
  } else {
    const loadingPageMsg = {
      id: 'EditProductPage.loadingProductData',
    };
    return (
      <Page title={intl.formatMessage(loadingPageMsg)} scrollingDisabled={scrollingDisabled} />
    );
  }
};

const mapStateToProps = state => {
  const page = state.EditProductPage;
  const { currentUser } = state.user;

  const getOwnProduct = id => {
    const products = getMarketplaceEntities(state, [{ id, type: 'catalog-product' }]);
    return products.length === 1 ? products[0] : null;
  };

  return {
    currentUser,
    fetchInProgress: false,
    getOwnProduct,
    page,
    scrollingDisabled: isScrollingDisabled(state)
  };
};

const mapDispatchToProps = dispatch => ({
  onUpdateProduct: (values) => dispatch(requestUpdateProduct(values)),
  onCreateProduct: values => dispatch(requestCreateProduct(values)),
  onImageUpload: data => dispatch(requestImageUpload(data)),
  onManageDisableScrolling: (componentId, disableScrolling) =>
    dispatch(manageDisableScrolling(componentId, disableScrolling)),
  onUpdateImageOrder: imageOrder => dispatch(updateImageOrder(imageOrder)),
  onRemoveProductImage: imageId => dispatch(removeProductImage(imageId)),
  onPrependNewProduct: newProductRef => dispatch(prependNewProduct(newProductRef)),
});

const EditProductPage = compose(
  withRouter,
  connect(
    mapStateToProps,
    mapDispatchToProps
  )
)(injectIntl(EditProductPageComponent));

EditProductPage.loadData = loadData;

export default EditProductPage;
